/**
 * WasmGuaranteeTopology — visual trust-zone map for TypeScript and TS-WASM guarantees.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { Boxes, Cpu, ShieldCheck } from "lucide-react";
import type {
  NativeWasmBoundaryReceipt,
  WasmKernelValidationReceipt,
  WasmSeamBridgeValidationReceipt,
} from "@csk/wasm";
import type {
  RuntimeGuaranteeComparison,
  RuntimeHardenedTheoremCode,
  WasmHardeningProbe,
} from "../domain/index.js";
import { FONT, GRAY, PRIMARY, SAFE, WARN } from "../design-system/tokens.js";

const RUNTIME_THEOREM_CODES: readonly RuntimeHardenedTheoremCode[] = ["R1", "R2", "I1", "I2", "C"];

export interface WasmGuaranteeTopologyProps {
  readonly rows: readonly RuntimeGuaranteeComparison[];
  readonly probes: readonly WasmHardeningProbe[];
  readonly backend: string;
  readonly loading?: boolean;
  readonly contractValidation?: WasmKernelValidationReceipt;
  readonly seamBridgeValidation?: WasmSeamBridgeValidationReceipt;
  readonly nativeBoundary?: NativeWasmBoundaryReceipt;
}

function runtimeProbeIndex(
  probes: readonly WasmHardeningProbe[],
): ReadonlyMap<RuntimeHardenedTheoremCode, WasmHardeningProbe> {
  return new Map(probes.map((probe) => [probe.theoremCode, probe]));
}

function runtimeRows(
  rows: readonly RuntimeGuaranteeComparison[],
): readonly RuntimeGuaranteeComparison[] {
  return RUNTIME_THEOREM_CODES.map((code) => {
    const row = rows.find((entry) => entry.code === code);
    if (row === undefined) {
      throw new Error(`TS-WASM topology is missing ${code}`);
    }
    return row;
  });
}

function LaneHeader({
  icon,
  title,
  subtitle,
  tone,
}: {
  readonly icon: React.ReactNode;
  readonly title: string;
  readonly subtitle: string;
  readonly tone: string;
}): React.JSX.Element {
  return (
    <div
      style={{
        background: `${tone}12`,
        border: `1px solid ${tone}40`,
        borderRadius: 8,
        minHeight: 116,
        padding: "0.875rem",
      }}
    >
      <div style={{ alignItems: "center", color: tone, display: "flex", gap: 8 }}>
        {icon}
        <strong style={{ fontSize: 14 }}>{title}</strong>
      </div>
      <p style={{ color: GRAY[700], fontSize: 13, lineHeight: 1.45, margin: "0.625rem 0 0" }}>
        {subtitle}
      </p>
    </div>
  );
}

function TheoremChip({
  code,
  name,
  tone,
}: {
  readonly code: string;
  readonly name: string;
  readonly tone: string;
}): React.JSX.Element {
  return (
    <span
      style={{
        alignItems: "center",
        background: `${tone}12`,
        border: `1px solid ${tone}35`,
        borderRadius: 999,
        color: GRAY[900],
        display: "inline-flex",
        fontSize: 12,
        fontWeight: 700,
        gap: 6,
        padding: "4px 8px",
        whiteSpace: "nowrap",
      }}
      title={name}
    >
      <span style={{ color: tone, fontFamily: FONT.mono }}>{code}</span>
      {name}
    </span>
  );
}

function ProbeStatus({
  probe,
}: {
  readonly probe: WasmHardeningProbe | undefined;
}): React.JSX.Element {
  if (probe === undefined) {
    return <span style={{ color: GRAY[500], fontSize: 12, fontWeight: 700 }}>probe not wired</span>;
  }
  const verified = probe.accepted && probe.adversarialCheckRejected;
  const tone = verified ? SAFE[700] : WARN[700];
  return (
    <span
      style={{
        alignItems: "center",
        color: tone,
        display: "inline-flex",
        fontSize: 12,
        fontWeight: 800,
        gap: 6,
      }}
    >
      <ShieldCheck size={14} />
      {verified ? "adversarial check refused" : "probe needs review"}
    </span>
  );
}

function RuntimeTransferRow({
  row,
  probe,
}: {
  readonly row: RuntimeGuaranteeComparison;
  readonly probe: WasmHardeningProbe | undefined;
}): React.JSX.Element {
  return (
    <div
      style={{
        alignItems: "stretch",
        display: "grid",
        gap: "0.75rem",
        gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
      }}
    >
      <div
        style={{
          background: "white",
          border: `1px solid ${WARN[300]}`,
          borderRadius: 8,
          padding: "0.75rem",
        }}
      >
        <TheoremChip code={row.code} name={row.name} tone={WARN[700]} />
        <div style={{ color: GRAY[600], fontFamily: FONT.mono, fontSize: 12, marginTop: 8 }}>
          {row.rawTs.fidelity}
        </div>
      </div>
      <div
        style={{
          alignItems: "center",
          background: GRAY[50],
          border: `1px dashed ${GRAY[300]}`,
          borderRadius: 8,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minHeight: 74,
          padding: "0.75rem",
          textAlign: "center",
        }}
      >
        <strong style={{ color: GRAY[900], fontSize: 13 }}>same theorem API</strong>
        <span style={{ color: SAFE[700], fontFamily: FONT.mono, fontSize: 12, marginTop: 4 }}>
          JS heap -&gt; WASM linear memory
        </span>
        <div style={{ marginTop: 8 }}>
          <ProbeStatus probe={probe} />
        </div>
      </div>
      <div
        style={{
          background: "white",
          border: `1px solid ${SAFE[300]}`,
          borderRadius: 8,
          padding: "0.75rem",
        }}
      >
        <TheoremChip code={row.code} name={row.name} tone={SAFE[700]} />
        <div style={{ color: GRAY[600], fontFamily: FONT.mono, fontSize: 12, marginTop: 8 }}>
          {row.tsWasm.fidelity}
        </div>
      </div>
    </div>
  );
}

export function WasmGuaranteeTopology({
  rows,
  probes,
  backend,
  loading = false,
  contractValidation,
  seamBridgeValidation,
  nativeBoundary,
}: WasmGuaranteeTopologyProps): React.JSX.Element {
  const compileTimeRows = rows.filter((row) => !row.hardenedByWasm);
  const hardenedRows = runtimeRows(rows);
  const probesByCode = runtimeProbeIndex(probes);
  const backendLabel = loading ? `${backend} while loading` : backend;
  const validationCount =
    contractValidation?.probes.filter(
      (entry) => entry.governedPathAccepted && entry.forbiddenPathRefused,
    ).length ?? 0;
  const validationTotal = contractValidation?.probes.length ?? 0;
  const nativeLabel =
    nativeBoundary?.nativeArtifactLoaded === true ? "native artifact loaded" : "pure-TS fallback";
  const seamCount =
    seamBridgeValidation?.probes.filter(
      (entry) =>
        entry.descriptorMatches &&
        entry.snfTraceAccepted &&
        entry.governedBridgeAdmitted &&
        entry.forbiddenSquareRefused,
    ).length ?? 0;
  const seamTotal = seamBridgeValidation?.seamCount ?? 0;
  const refusedGenerators = seamBridgeValidation?.forbiddenGeneratorsRefused.join("-") ?? "";

  return (
    <section
      aria-label="TS-WASM trust-zone topology"
      style={{
        background: "white",
        border: `1px solid ${GRAY[300]}`,
        borderRadius: 8,
        padding: "1rem",
      }}
    >
      <div style={{ alignItems: "center", display: "flex", gap: 10, marginBottom: 14 }}>
        <Boxes color={SAFE[700]} size={20} />
        <div>
          <h3 style={{ color: GRAY[900], fontSize: 18, margin: 0 }}>Trust-zone relocation map</h3>
          <div style={{ color: GRAY[600], fontSize: 13, marginTop: 2 }}>
            Active backend: <span style={{ fontFamily: FONT.mono }}>{backendLabel}</span>
            {contractValidation !== undefined && (
              <>
                {" "}
                | contract probes:{" "}
                <span style={{ color: contractValidation.passed ? SAFE[700] : WARN[700] }}>
                  {validationCount}/{validationTotal}
                </span>
              </>
            )}
            {nativeBoundary !== undefined && (
              <>
                {" "}
                | boundary:{" "}
                <span
                  style={{
                    color: nativeBoundary.nativeArtifactLoaded ? SAFE[700] : GRAY[700],
                    fontFamily: FONT.mono,
                  }}
                >
                  {nativeLabel}
                </span>
              </>
            )}
            {seamBridgeValidation !== undefined && (
              <>
                {" "}
                | seams:{" "}
                <span style={{ color: seamBridgeValidation.passed ? SAFE[700] : WARN[700] }}>
                  {seamCount}/{seamTotal}
                </span>{" "}
                | squares:{" "}
                <span style={{ color: SAFE[700], fontFamily: FONT.mono }}>
                  {refusedGenerators} refused
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gap: "0.75rem",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          marginBottom: "0.875rem",
        }}
      >
        <LaneHeader
          icon={<Cpu size={18} />}
          title="Zone 1A: TypeScript types"
          subtitle="IMMUNE theorem crossings remain type-level; the forbidden construction is absent before runtime."
          tone={PRIMARY[600]}
        />
        <LaneHeader
          icon={<Boxes size={18} />}
          title="Zone 1B: JavaScript heap"
          subtitle="Raw TypeScript runtime contracts keep mutable theorem state in ordinary JS objects."
          tone={WARN[700]}
        />
        <LaneHeader
          icon={<ShieldCheck size={18} />}
          title="Zone 2: WASM memory"
          subtitle="TS-WASM routes CONTAINED and INERT theorem state through the isolated bridge boundary."
          tone={SAFE[700]}
        />
      </div>

      <div
        style={{
          background: PRIMARY[100],
          border: `1px solid ${PRIMARY[300]}`,
          borderRadius: 8,
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: "0.875rem",
          padding: "0.75rem",
        }}
      >
        {compileTimeRows.map((row) => (
          <TheoremChip key={row.code} code={row.code} name={row.name} tone={PRIMARY[600]} />
        ))}
      </div>

      <div style={{ display: "grid", gap: "0.625rem" }}>
        {hardenedRows.map((row) => (
          <RuntimeTransferRow
            key={row.code}
            row={row}
            probe={probesByCode.get(row.code as RuntimeHardenedTheoremCode)}
          />
        ))}
      </div>
    </section>
  );
}
